// Hand-written PNG encoder (§9): 8 or 16 bit, RGB or RGBA, iCCP, pHYs, iTXt XMP.
// Pure: no DOM. Used by the export worker and by scripts/make-fixtures.ts (Node).
import { zlibSync } from 'fflate';
import { crc32 } from './crc32';

export type PngInput = {
  width: number;
  height: number;
  /** Interleaved RGBA samples, straight (non-premultiplied) alpha. Uint8 for 8-bit, Uint16 for 16-bit. */
  data: Uint8Array | Uint16Array;
  bitDepth: 8 | 16;
  /** 'auto' writes RGB when every alpha is at maximum, RGBA otherwise (§9). */
  channels?: 'auto' | 'rgb' | 'rgba';
  icc?: { name: string; profile: Uint8Array };
  dpi?: number;
  xmp?: string;
  level?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
};

const SIGNATURE = new Uint8Array([137, 80, 78, 71, 13, 10, 26, 10]);
const IDAT_SPLIT = 1 << 20; // 1 MB per IDAT chunk

export function chunk(type: string, body: Uint8Array): Uint8Array {
  const out = new Uint8Array(12 + body.length);
  const dv = new DataView(out.buffer);
  dv.setUint32(0, body.length);
  for (let i = 0; i < 4; i++) out[4 + i] = type.charCodeAt(i);
  out.set(body, 8);
  dv.setUint32(8 + body.length, crc32(out.subarray(4, 8 + body.length)));
  return out;
}

const latin1 = (s: string) =>
  Uint8Array.from(s, (c) => {
    const code = c.charCodeAt(0);
    if (code > 255) throw new Error('PNG keyword must be Latin-1');
    return code;
  });

export function isOpaque(data: Uint8Array | Uint16Array, bitDepth: 8 | 16): boolean {
  const max = bitDepth === 16 ? 65535 : 255;
  for (let i = 3; i < data.length; i += 4) if (data[i] !== max) return false;
  return true;
}

/** Paeth predictor, PNG spec 9.4. */
function paeth(a: number, b: number, c: number): number {
  const p = a + b - c;
  const pa = Math.abs(p - a);
  const pb = Math.abs(p - b);
  const pc = Math.abs(p - c);
  return pa <= pb && pa <= pc ? a : pb <= pc ? b : c;
}

/** Builds the filtered scanline stream (filter type 4, Paeth, on every row). */
export function filteredScanlines(
  width: number,
  height: number,
  data: Uint8Array | Uint16Array,
  bitDepth: 8 | 16,
  channels: 3 | 4,
): Uint8Array {
  const bps = bitDepth / 8; // bytes per sample
  const bpp = channels * bps; // bytes per pixel
  const stride = width * bpp;
  const raw = new Uint8Array(stride); // current row, big-endian bytes
  const prev = new Uint8Array(stride);
  const out = new Uint8Array((stride + 1) * height);
  for (let y = 0; y < height; y++) {
    // Serialize row y to big-endian bytes, dropping alpha when channels === 3.
    let o = 0;
    const rowStart = y * width * 4;
    for (let x = 0; x < width; x++) {
      const s = rowStart + x * 4;
      for (let c = 0; c < channels; c++) {
        const v = data[s + c]!;
        if (bps === 2) {
          raw[o++] = v >>> 8;
          raw[o++] = v & 255;
        } else raw[o++] = v;
      }
    }
    const base = y * (stride + 1);
    out[base] = 4;
    for (let i = 0; i < stride; i++) {
      const a = i >= bpp ? raw[i - bpp]! : 0;
      const b = prev[i]!;
      const c = i >= bpp ? prev[i - bpp]! : 0;
      out[base + 1 + i] = (raw[i]! - paeth(a, b, c)) & 255;
    }
    prev.set(raw);
  }
  return out;
}

export function encodePng(input: PngInput): Uint8Array {
  const { width, height, data, bitDepth } = input;
  if (data.length !== width * height * 4)
    throw new Error(`PNG data length ${data.length} != ${width}x${height}x4`);
  if (bitDepth === 16 && !(data instanceof Uint16Array)) throw new Error('16-bit PNG needs Uint16Array data');
  if (bitDepth === 8 && !(data instanceof Uint8Array)) throw new Error('8-bit PNG needs Uint8Array data');
  const mode = input.channels ?? 'auto';
  const channels: 3 | 4 = mode === 'rgb' ? 3 : mode === 'rgba' ? 4 : isOpaque(data, bitDepth) ? 3 : 4;

  const parts: Uint8Array[] = [SIGNATURE];
  const ihdr = new Uint8Array(13);
  const dv = new DataView(ihdr.buffer);
  dv.setUint32(0, width);
  dv.setUint32(4, height);
  ihdr[8] = bitDepth;
  ihdr[9] = channels === 4 ? 6 : 2; // colour type: 6 = RGBA, 2 = RGB
  ihdr[10] = 0; // deflate
  ihdr[11] = 0; // adaptive filtering
  ihdr[12] = 0; // no interlace
  parts.push(chunk('IHDR', ihdr));

  if (input.icc) {
    const name = latin1(input.icc.name.slice(0, 79));
    const comp = zlibSync(input.icc.profile, { level: 9 });
    const body = new Uint8Array(name.length + 2 + comp.length);
    body.set(name, 0);
    body[name.length] = 0; // null separator
    body[name.length + 1] = 0; // compression method: deflate
    body.set(comp, name.length + 2);
    parts.push(chunk('iCCP', body));
  }
  if (input.dpi && input.dpi > 0) {
    const ppm = Math.round(input.dpi / 0.0254);
    const body = new Uint8Array(9);
    const bdv = new DataView(body.buffer);
    bdv.setUint32(0, ppm);
    bdv.setUint32(4, ppm);
    body[8] = 1; // unit: metre
    parts.push(chunk('pHYs', body));
  }
  if (input.xmp) {
    const kw = latin1('XML:com.adobe.xmp');
    const text = new TextEncoder().encode(input.xmp);
    // keyword \0 compressionFlag(0) compressionMethod(0) languageTag\0 translatedKeyword\0 text
    const body = new Uint8Array(kw.length + 5 + text.length);
    body.set(kw, 0);
    body.set(text, kw.length + 5);
    parts.push(chunk('iTXt', body));
  }

  const z = zlibSync(filteredScanlines(width, height, data, bitDepth, channels), { level: input.level ?? 6 });
  for (let off = 0; off < z.length; off += IDAT_SPLIT)
    parts.push(chunk('IDAT', z.subarray(off, Math.min(z.length, off + IDAT_SPLIT))));
  parts.push(chunk('IEND', new Uint8Array(0)));

  let total = 0;
  for (const p of parts) total += p.length;
  const out = new Uint8Array(total);
  let o = 0;
  for (const p of parts) {
    out.set(p, o);
    o += p.length;
  }
  return out;
}

/** 8-bit grayscale PNG (colour type 0), e.g. the background matte cache (§8). Sub filter on every row. */
export function encodeGray8(width: number, height: number, data: Uint8Array): Uint8Array {
  if (data.length !== width * height)
    throw new Error(`gray data length ${data.length} != ${width}x${height}`);
  const raw = new Uint8Array((width + 1) * height);
  for (let y = 0; y < height; y++) {
    const o = y * (width + 1);
    raw[o] = 1; // Sub
    for (let x = 0; x < width; x++) {
      const v = data[y * width + x]!;
      raw[o + 1 + x] = (v - (x ? data[y * width + x - 1]! : 0)) & 255;
    }
  }
  const ihdr = new Uint8Array(13);
  const dv = new DataView(ihdr.buffer);
  dv.setUint32(0, width);
  dv.setUint32(4, height);
  ihdr[8] = 8;
  ihdr[9] = 0;
  const parts = [
    SIGNATURE,
    chunk('IHDR', ihdr),
    chunk('IDAT', zlibSync(raw, { level: 6 })),
    chunk('IEND', new Uint8Array(0)),
  ];
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let o = 0;
  for (const p of parts) {
    out.set(p, o);
    o += p.length;
  }
  return out;
}
